import * as React from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { getAuctionCalendarUrlWithParams } from '@/utils/urls';
import { H4 } from '@liveauctioneers/hammer-ui-core/text';
import { modifyAuctionCalendarParamsSaleStart, modifyParamsRemovePage } from '@/utils/queryParams';
import { SearchSortAndDirection } from '@/types/search/enums/sortTypes';
import { Select } from '@liveauctioneers/hammer-ui-core/select';
import { useNavigate } from 'react-router-dom';
import isEqual from 'react-fast-compare';
import PageTitle from '@/components/PageTitle/PageTitle';
import styled from 'styled-components';

type Props = {
    queryParams: { page: number; pageNumber: number; sort: SearchSortAndDirection };
    totalFound: number;
};

const AuctionCalendarPageHeader: React.FC<Props> = ({ queryParams, totalFound }: Props) => {
    const intl = useIntl();
    const navigate = useNavigate();
    const soonest = Boolean(queryParams.sort) && queryParams.sort === SearchSortAndDirection.TimeMostRecent;

    const onChangeSortOption = (event) => {
        if (event.value === SearchSortAndDirection.TimeStartingSoonest && soonest) {
            return;
        }
        const getSoonest = event.value === SearchSortAndDirection.TimeStartingSoonest;
        let newParams = modifyAuctionCalendarParamsSaleStart(queryParams, getSoonest);
        newParams = modifyParamsRemovePage(newParams);
        const url = getAuctionCalendarUrlWithParams(newParams);
        navigate(url);
    };

    const options = [
        {
            label: intl.formatMessage({ id: 'auctionCalendar.sort.trending' }),
            value: 'trending',
        },
        {
            label: intl.formatMessage({
                id: `sorts.${SearchSortAndDirection.TimeStartingSoonest}`,
            }),
            value: SearchSortAndDirection.TimeStartingSoonest,
        },
    ];

    return (
        <StyledHeader>
            <StyledPageTitleRow>
                <StyledPageTitle title={<FormattedMessage id="auction_calendar" />} />
            </StyledPageTitleRow>
            <StyledCategoryDesktopBar>
                <StyledDesktopContent key="auction-calendar-search-page-header">
                    <H4 key="auction-calendar-search-bar-text">
                        <FormattedMessage
                            id="auctionCalendar.searchBarText"
                            values={{
                                count: (
                                    <FormattedNumber
                                        key="total_found"
                                        value={totalFound}
                                    />
                                ),
                            }}
                        />
                    </H4>
                    <StyledSortContainer>
                        <Select
                            className="min-w-[246px] xsMax:mt-xs"
                            data-testid="choose-sort-dropdown"
                            isInsideLabel
                            labelText={<FormattedMessage id="auctionCalendar.sort.title" />}
                            name="sort"
                            onChange={onChangeSortOption}
                            options={options}
                            placeholder={intl.formatMessage({ id: 'select' })}
                            small
                            value={soonest ? options[1] : options[0]}
                        />
                    </StyledSortContainer>
                </StyledDesktopContent>
            </StyledCategoryDesktopBar>
        </StyledHeader>
    );
};

export default React.memo<Props>(AuctionCalendarPageHeader, isEqual);

const StyledHeader = styled.div`
    max-width: 1200px;
    align-self: center;
    width: calc(100% - 40px);
`;

const StyledCategoryDesktopBar = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    margin: 0;
    flex: 1 1 auto;
    padding: 20px 0;
    position: relative;

    h4 {
        margin-right: 10px;
        white-space: nowrap;
        flex: 0 0 auto;
    }
`;

const StyledPageTitleRow = styled.div`
    align-items: flex-end;
    display: flex;
    justify-content: space-between;
    flex-direction: row;
    margin: 17px 0 25px;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        margin-bottom: 10px;
    }
`;

const StyledPageTitle = styled(PageTitle)`
    align-items: center;
    &&& {
        margin: 0;
        display: flex;

        @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
            flex-direction: row;
            align-items: baseline;
            flex-wrap: wrap;
        }
    }
`;

const StyledDesktopContent = styled.div`
    padding: 0;
    background-color: ${({ theme }) => theme.colors.white};
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        flex-direction: column;
        align-items: flex-start;
    }
`;

const StyledSortContainer = styled.div`
    display: flex;
    flex: 1;
    justify-content: flex-end;
`;
